import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { BookOpen, GraduationCap, FileText, BarChart3, MessageSquare, LogOut, Trash2, CheckCircle, XCircle, Quote } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { getAuthHeaders } from "@/utils/auth";
import Header from "@/components/Header";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDataProtectionOpen, setIsDataProtectionOpen] = useState(false);
  const [isEssentialCookiesOpen, setIsEssentialCookiesOpen] = useState(false);

  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        const response = await axios.get(`${API}/submissions/my`, {
          headers: getAuthHeaders(),
        });
        setSubmissions(response.data);
      } catch (error) {
        toast.error("Failed to load submissions");
      } finally {
        setLoading(false);
      }
    };
    fetchSubmissions();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleDeleteSubmission = async (submissionId, submissionType) => {
    if (!window.confirm("Are you sure you want to delete this submission? This action cannot be undone.")) {
      return;
    }

    try {
      // Map submission types to their endpoint names (matching the types from allSubmissions array)
      const typeMap = {
        "Methodological Support": "methodological",
        "Training Request": "training",
        "Grant Application": "grants",
        "Data Analysis": "data-analysis",
        "Thesis & Dissertation": "thesis-dissertation",
        "Clinical Research Advisory": "clinical-research",
        "Contact us": "general-enquiry"
      };

      const endpoint = typeMap[submissionType];
      
      if (!endpoint) {
        console.error("Unknown submission type:", submissionType);
        toast.error("Cannot delete: Unknown submission type");
        return;
      }

      await axios.delete(`${API}/submissions/${endpoint}/${submissionId}`, {
        headers: getAuthHeaders(),
      });

      toast.success("Submission deleted successfully");
      // Refresh submissions list
      fetchSubmissions();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error.response?.data?.detail || "Failed to delete submission. Please try again.");
    }
  };

  const services = [
    {
      icon: BookOpen,
      title: "Research Training & Capacity Building",
      description: "Structured programmes to build strong methodological and analytical competence",
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      path: "/submit/training",
      type: "training",
    },
    {
      icon: GraduationCap,
      title: "Research Mentorship & Academic Guidance",
      description: "End-to-end mentorship for students, early-career researchers, and experienced academics",
      color: "text-teal-600",
      bgColor: "bg-teal-50",
      path: "/submit/thesis-dissertation",
      type: "thesis_dissertation",
    },
    {
      icon: BarChart3,
      title: "Data Analysis & Evidence Interpretation",
      description: "Expert analytical services with optional training for independent competence",
      color: "text-purple-600",
      bgColor: "bg-purple-50",
      path: "/submit/data-analysis",
      type: "data_analysis",
    },
    {
      icon: FileText,
      title: "Evidence Synthesis & Guideline Development Support",
      description: "High-quality systematic reviews, health technology assessments and evidence summaries",
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      path: "/submit/grant",
      type: "grants",
    },
    {
      icon: BookOpen,
      title: "Educational Excellence & Faculty Development",
      description: "Capacity-building for educators and institutions across health professions",
      color: "text-indigo-600",
      bgColor: "bg-indigo-50",
      path: "/submit/methodological",
      type: "methodological",
    },
    {
      icon: FileText,
      title: "Clinical Research Advisory Services",
      description: "Specialised support for clinical research planning and implementation",
      color: "text-red-600",
      bgColor: "bg-red-50",
      path: "/submit/clinical-research",
      type: "clinical_research",
    },
    {
      icon: MessageSquare,
      title: "Contact us",
      description: "Questions or need more information?",
      color: "text-gray-600",
      bgColor: "bg-gray-50",
      path: "/submit/general-enquiry",
      type: "general_enquiries",
    },
  ];

  const getStatusBadge = (status) => {
    const variants = {
      pending: { variant: "secondary", label: "Pending" },
      in_progress: { variant: "default", label: "In Progress" },
      completed: { variant: "success", label: "Completed" },
    };
    const config = variants[status] || variants.pending;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const allSubmissions = submissions
    ? [
        ...submissions.methodological.map(s => ({ ...s, type: 'Methodological Support' })),
        ...submissions.training.map(s => ({ ...s, type: 'Training Request' })),
        ...submissions.grants.map(s => ({ ...s, type: 'Grant Application' })),
        ...submissions.data_analysis.map(s => ({ ...s, type: 'Data Analysis' })),
        ...(submissions.thesis_dissertation || []).map(s => ({ ...s, type: 'Thesis & Dissertation' })),
        ...(submissions.clinical_research || []).map(s => ({ ...s, type: 'Clinical Research Advisory' })),
        ...submissions.general_enquiries.map(s => ({ ...s, type: 'General Enquiry' })),
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Services Grid */}
        <section className="mb-8 sm:mb-12">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6" >
            Submit a New Request
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4">
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <Card
                  key={service.title}
                  data-testid={`service-card-${service.title}`}
                  className="hover:shadow-lg transition-all duration-300 cursor-pointer border-0 bg-white"
                  onClick={() => navigate(service.path)}
                >
                  <CardHeader>
                    <div className={`w-12 h-12 rounded-lg ${service.bgColor} flex items-center justify-center mb-2`}>
                      <Icon className={`h-6 w-6 ${service.color}`} />
                    </div>
                    <CardTitle className="text-base">{service.title}</CardTitle>
                    <CardDescription className="text-sm">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Submissions List */}
        <section>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6" >
            Your Submissions
          </h2>
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : allSubmissions.length === 0 ? (
            <Card className="bg-white">
              <CardContent className="py-12 text-center">
                <p className="text-gray-500">No submissions yet. Create your first request above!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {allSubmissions.map((submission) => (
                <Card key={submission.id} data-testid={`submission-${submission.id}`} className="bg-white hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2 flex-wrap gap-2">
                          <CardTitle className="text-base sm:text-lg">{submission.type}</CardTitle>
                          {getStatusBadge(submission.status)}
                        </div>
                        <CardDescription className="text-sm text-gray-500">
                          Submitted on {formatDate(submission.created_at)}
                        </CardDescription>
                      </div>
                      <div className="flex flex-col space-y-2 w-full sm:w-auto mt-2 sm:mt-0">
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteSubmission(submission.id, submission.type)}
                          className="flex items-center justify-center space-x-1 text-xs sm:text-sm"
                          data-testid={`delete-btn-${index}`}
                        >
                          <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                          <span>Delete</span>
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <p className="text-sm text-gray-700">
                        <strong>Contact:</strong> {submission.contact_name} ({submission.contact_email})
                      </p>
                      {submission.contact_affiliation && (
                        <p className="text-sm text-gray-700">
                          <strong>Affiliation:</strong> {submission.contact_affiliation}
                        </p>
                      )}
                      {submission.research_title && (
                        <p className="text-sm text-gray-700">
                          <strong>Title:</strong> {submission.research_title}
                        </p>
                      )}
                      {submission.training_needs && (
                        <p className="text-sm text-gray-700">
                          <strong>Training Needs:</strong> {submission.training_needs}
                        </p>
                      )}
                      {submission.number_of_participants && (
                        <p className="text-sm text-gray-700">
                          <strong>Participants:</strong> {submission.number_of_participants}
                        </p>
                      )}
                      {submission.tentative_dates && (
                        <p className="text-sm text-gray-700">
                          <strong>Tentative Dates:</strong> {submission.tentative_dates}
                        </p>
                      )}
                      {submission.grant_idea && (
                        <p className="text-sm text-gray-700">
                          <strong>Grant Idea:</strong> {submission.grant_idea}
                        </p>
                      )}
                      {submission.results_description && (
                        <p className="text-sm text-gray-700">
                          <strong>Results:</strong> {submission.results_description}
                        </p>
                      )}
                      {submission.message && (
                        <p className="text-sm text-gray-700">
                          <strong>Message:</strong> {submission.message}
                        </p>
                      )}
                      {submission.affiliation_organisation && submission.type === 'General Enquiry' && (
                        <p className="text-sm text-gray-700">
                          <strong>Organisation:</strong> {submission.affiliation_organisation}
                        </p>
                      )}
                      {submission.deadline && (
                        <p className="text-sm text-gray-700">
                          <strong>Deadline:</strong> {submission.deadline}
                        </p>
                      )}
                      {submission.attached_file && (
                        <p className="text-sm text-gray-700">
                          <strong>Attached File:</strong>{" "}
                          <a
                            href={`${API}/files/${submission.attached_file.filename}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {submission.attached_file.original_filename}
                          </a>
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-6 sm:py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col items-center space-y-3 sm:space-y-4">
            <img 
              src="/images/logo.png" 
              alt="EviDent Research Consultancy" 
              className="h-12 sm:h-16 w-auto"
            />
            <div className="text-center space-y-1 sm:space-y-2">
              <p className="text-gray-400 text-xs sm:text-sm">
                © 2025 EviDent Research Consultancy Ltd. All rights reserved.
              </p>
              <p className="text-gray-500 text-xs sm:text-sm">
                Company Number: 15983903
              </p>
              <button
                onClick={() => setIsDataProtectionOpen(true)}
                className="text-blue-400 hover:text-blue-300 underline text-xs sm:text-sm transition-colors cursor-pointer"
              >
                Data Protection
              </button>
              <p className="text-gray-500 text-xs sm:text-sm">
                4-5, Russell Court, Palace Street, Plymouth, England, PL1 2AS
              </p>
              <button
                onClick={() => setIsEssentialCookiesOpen(true)}
                className="text-gray-400 hover:text-gray-300 underline text-xs mt-2 transition-colors cursor-pointer"
              >
                This site uses essential cookies for authentication and secure payments.
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Essential Cookies Privacy Policy Modal */}
      <Dialog open={isEssentialCookiesOpen} onOpenChange={setIsEssentialCookiesOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Essential Cookies Privacy Policy</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-gray-700 mt-4">
            <p className="text-sm text-gray-600">Last updated: 17 November 2025</p>
            
            <p className="text-sm sm:text-base leading-relaxed">
              EVIDENT RESEARCH CONSULTANCY LTD ("we", "us", "our") uses only essential cookies on this website. 
              Essential cookies are necessary for the website to function securely and correctly, and they cannot 
              be switched off in our systems.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-900 mt-6">What Are Essential Cookies?</h3>
            <p className="text-sm sm:text-base leading-relaxed">
              Essential cookies are small text files placed on your device to enable core website features such as 
              page navigation, security, loading times, session management, and accessibility. Without these cookies, 
              the website may not operate properly.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-900 mt-6">Why We Use Essential Cookies</h3>
            <p className="text-sm sm:text-base leading-relaxed">We use essential cookies to:</p>
            <ul className="list-disc pl-6 space-y-1 text-sm sm:text-base">
              <li>enable basic site functionality;</li>
              <li>maintain the security and stability of the website;</li>
              <li>remember your session or preferences needed to deliver the service;</li>
              <li>ensure pages load efficiently on your device.</li>
            </ul>
            <p className="text-sm sm:text-base leading-relaxed mt-2">
              These cookies do not collect personal data for marketing purposes, analytics, profiling, or advertising.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-900 mt-6">Your Consent</h3>
            <p className="text-sm sm:text-base leading-relaxed">
              Because essential cookies are strictly necessary for the functioning of this website, they do not require 
              your consent under UK GDPR and the Privacy and Electronic Communications Regulations (PECR). They are 
              automatically placed when you visit the site.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-900 mt-6">How Long Are Essential Cookies Stored?</h3>
            <p className="text-sm sm:text-base leading-relaxed">
              Essential cookies may be stored only for the duration of your session or for a short period afterwards, 
              depending on their purpose.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-900 mt-6">Managing Cookies</h3>
            <p className="text-sm sm:text-base leading-relaxed">
              You may be able to block or delete essential cookies through your browser settings. However, doing so 
              may cause parts of the website to stop working correctly.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-900 mt-6">Contact Us</h3>
            <p className="text-sm sm:text-base leading-relaxed">
              If you have any questions about our use of essential cookies, please contact us at:{" "}
              <a href="mailto:sumanthkumbargerenagraj@gmail.com" className="text-blue-600 hover:text-blue-800 underline">
                sumanthkumbargerenagraj@gmail.com
              </a>
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Data Protection Modal */}
      <Dialog open={isDataProtectionOpen} onOpenChange={setIsDataProtectionOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Data Protection & Cookie Policy</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-gray-700 mt-4">
            <h3 className="text-lg font-semibold text-gray-900">Data Protection</h3>
            <p className="text-sm sm:text-base leading-relaxed">
              We handle personal information, study data and manuscripts with strict confidentiality and in full 
              alignment with the EU and UK GDPR. We collect only what is necessary, apply pseudonymisation whenever 
              possible, secure data through robust technical and organisational measures, and retain it only for as 
              long as required. When we process data on behalf of clients, we follow their written instructions and 
              enter into appropriate data-processing terms.
            </p>
            <p className="text-sm sm:text-base leading-relaxed">
              If personal data ever needs to be transferred outside the UK or the European Economic Area, we will 
              only do so using legally approved safeguards. These may include an adequacy decision or the required 
              contractual protections, such as the EU Standard Contractual Clauses or the UK International Data 
              Transfer Agreement. Individuals can exercise their rights to access, rectification, erasure, 
              restriction, portability and objection at any time.
            </p>
            
            <h3 className="text-lg font-semibold text-gray-900 mt-6">Cookies and Similar Technologies</h3>
            <p className="text-sm sm:text-base leading-relaxed">
              We use cookies to enable essential functionality on our website. These cookies are strictly necessary 
              for the website to function properly and cannot be disabled.
            </p>
            
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Essential Cookies:</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <strong>Session Management:</strong> We use a secure session cookie (session_token) to keep you 
                  logged in and protect your account. This cookie expires after 7 days of inactivity.
                </li>
                <li>
                  <strong>Authentication Services:</strong> When you use Google Sign In, PayPal, or Stripe, these 
                  services may set their own cookies necessary for secure authentication and payment processing.
                </li>
              </ul>
            </div>
            
            <p className="text-sm sm:text-base leading-relaxed">
              By using our services, you consent to the use of these essential cookies. You can manage cookies 
              through your browser settings, though disabling them may prevent you from accessing certain features 
              of our website.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;